// FE-PINV-073 — Columns popover (visibility + reorder + save-as-view).
//
// Anchored to the List view's `Columns` button. Owns column
// visibility, drag-to-reorder, density (canonical control), Reset to
// default, and Save as view (delegates to FE-PINV-065's save flow).
//
// Drag-to-reorder uses native HTML5 drag-and-drop — matches the
// existing pattern in <OpportunityCard> (RenewalBoard/OpportunityCard.tsx).
// No new top-level dnd dependency.

import { useEffect, useMemo, useRef, useState } from "react";

import type { Density } from "./filterState";
import {
  DEFAULT_COLUMN_ORDER,
  DEFAULT_DENSITY,
  DEFAULT_VISIBLE_COLUMNS,
  type ListColumnId,
} from "./listUrlState";


const COLUMN_LABELS: Record<ListColumnId, string> = {
  select: "Select",
  prospect: "Prospect",
  opportunity: "Opportunity",
  stage: "Stage",
  type: "Type",
  lives: "Lives",
  renewal: "Renewal",
  producer: "Producer",
  est_arr: "Est ARR",
  last_activity: "Last activity",
  next_step: "Next step",
  alerts: "Alerts",
};


export interface ColumnsPopoverProps {
  visible: ListColumnId[];
  order: ListColumnId[];
  density: Density;
  onChange: (next: {
    visible: ListColumnId[];
    order: ListColumnId[];
    density: Density;
  }) => void;
  /** Opens the SavedView save-flow with the current columns_json +
   *  density pre-filled. Host (RenewalBoardPanel) routes this through
   *  W2's <SavedViewSwitcher>'s save path so the modal is not
   *  duplicated. */
  onSaveAsView: (columns: {
    visible: ListColumnId[];
    order: ListColumnId[];
    density: Density;
  }) => void;
  onClose: () => void;
}


export function ColumnsPopover({
  visible, order, density, onChange, onSaveAsView, onClose,
}: ColumnsPopoverProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click + Esc.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const [dragSrc, setDragSrc] = useState<ListColumnId | null>(null);

  const visibleSet = useMemo(() => new Set(visible), [visible]);

  // Display the popover in the canonical order — ``select`` is force-
  // pinned to position 0 and not hideable.
  const orderWithoutSelect = order.filter((c) => c !== "select");

  function toggleVisibility(col: ListColumnId) {
    if (col === "select") return; // never hide select
    const nextSet = new Set(visibleSet);
    if (nextSet.has(col)) nextSet.delete(col);
    else nextSet.add(col);
    onChange({
      visible: order.filter((c) => nextSet.has(c)),
      order,
      density,
    });
  }

  function onDragStart(col: ListColumnId) {
    setDragSrc(col);
  }

  function onDragOver(e: React.DragEvent<HTMLLIElement>) {
    e.preventDefault();
  }

  function onDrop(targetCol: ListColumnId) {
    if (!dragSrc || dragSrc === targetCol) {
      setDragSrc(null);
      return;
    }
    const reordered = reorderColumns(order, dragSrc, targetCol);
    onChange({
      visible: reordered.filter((c) => visibleSet.has(c)),
      order: reordered,
      density,
    });
    setDragSrc(null);
  }

  function setDensity(next: Density) {
    onChange({ visible, order, density: next });
  }

  function resetToDefault() {
    onChange({
      visible: [...DEFAULT_VISIBLE_COLUMNS],
      order: [...DEFAULT_COLUMN_ORDER],
      density: DEFAULT_DENSITY,
    });
  }

  return (
    <div
      ref={containerRef}
      data-testid="columns-popover"
      role="dialog"
      aria-label="Columns"
      className="absolute right-0 z-30 mt-2 flex w-72 flex-col rounded-md border border-border-default bg-raised shadow-overlay"
    >
      {/* Density section */}
      <section className="border-b border-border-subtle p-2">
        <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
          Density
        </h3>
        <div
          role="group"
          aria-label="Row density"
          className="inline-flex overflow-hidden rounded-md border border-border-subtle"
        >
          <button
            type="button"
            data-testid="columns-popover-density-comfortable"
            aria-pressed={density === "comfortable"}
            onClick={() => setDensity("comfortable")}
            className={[
              "px-2 py-1 text-xs",
              density === "comfortable"
                ? "bg-teal-tint text-text-primary"
                : "bg-raised text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            Comfortable
          </button>
          <button
            type="button"
            data-testid="columns-popover-density-compact"
            aria-pressed={density === "compact"}
            onClick={() => setDensity("compact")}
            className={[
              "px-2 py-1 text-xs",
              density === "compact"
                ? "bg-teal-tint text-text-primary"
                : "bg-raised text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            Compact
          </button>
        </div>
      </section>

      {/* Column list */}
      <section className="flex max-h-72 flex-col overflow-y-auto p-1">
        <h3 className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
          Columns
        </h3>
        <ul data-testid="columns-popover-list" className="flex flex-col">
          {orderWithoutSelect.map((col) => {
            const checked = visibleSet.has(col);
            const isDragging = dragSrc === col;
            return (
              <li
                key={col}
                draggable
                onDragStart={() => onDragStart(col)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(col)}
                data-testid={`columns-popover-row-${col}`}
                data-dragging={isDragging}
                className={[
                  "flex cursor-grab items-center gap-2 rounded px-1 py-1 text-sm",
                  isDragging ? "opacity-50" : "hover:bg-recessed",
                ].join(" ")}
              >
                <span aria-hidden="true" className="text-text-tertiary">⋮⋮</span>
                <input
                  type="checkbox"
                  data-testid={`columns-popover-visible-${col}`}
                  aria-label={`Show ${COLUMN_LABELS[col]} column`}
                  checked={checked}
                  onChange={() => toggleVisibility(col)}
                />
                <span className="flex-1 text-text-primary">
                  {COLUMN_LABELS[col]}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Footer */}
      <footer className="flex items-center justify-between gap-2 border-t border-border-subtle p-2">
        <button
          type="button"
          onClick={resetToDefault}
          data-testid="columns-popover-reset"
          className="text-xs text-text-secondary underline-offset-2 hover:underline"
        >
          Reset to default
        </button>
        <button
          type="button"
          onClick={() => onSaveAsView({ visible, order, density })}
          data-testid="columns-popover-save-as-view"
          className="rounded-md border border-border-default bg-raised px-2 py-1 text-xs font-medium text-text-primary hover:border-text-tertiary"
        >
          Save as view
        </button>
      </footer>
    </div>
  );
}


// ── Helpers ─────────────────────────────────────────────────────────


/** Reorder the canonical column array by moving ``src`` to ``target``'s
 *  position. ``select`` is never moved — it stays at position 0
 *  regardless of the request. */
export function reorderColumns(
  order: ListColumnId[],
  src: ListColumnId,
  target: ListColumnId,
): ListColumnId[] {
  if (src === "select" || target === "select") return order;
  const without = order.filter((c) => c !== src);
  const tgtIdx = without.indexOf(target);
  if (tgtIdx === -1) return order;
  // Insert ``src`` at ``target``'s position.
  return [
    ...without.slice(0, tgtIdx),
    src,
    ...without.slice(tgtIdx),
  ];
}
